import { db } from '../firebase';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  limit
} from 'firebase/firestore';
import { createRatingNotification } from './notificationService';

/**
 * Rating Service for managing student ratings and reviews
 */

/**
 * Submit a rating for a student
 * @param {Object} ratingData - Rating data
 * @param {string} ratingData.studentId - Student ID being rated
 * @param {string} ratingData.companyId - Company ID giving the rating
 * @param {string} ratingData.projectId - Project ID context
 * @param {number} ratingData.overallRating - Overall rating (1-5)
 * @param {Object} ratingData.categoryRatings - Category-specific ratings
 * @param {string} ratingData.review - Written review
 * @param {boolean} ratingData.wouldWorkAgain - Would work with student again
 * @returns {Promise<string>} - Rating ID
 */
export const submitStudentRating = async (ratingData) => {
  try {
    // Validate rating data
    if (!ratingData.studentId || !ratingData.companyId || !ratingData.projectId) {
      throw new Error('Missing required rating data');
    }

    if (ratingData.overallRating < 1 || ratingData.overallRating > 5) {
      throw new Error('Overall rating must be between 1 and 5');
    }

    // Check if rating already exists for this combination
    const existingRatingQuery = query(
      collection(db, 'studentRatings'),
      where('studentId', '==', ratingData.studentId),
      where('companyId', '==', ratingData.companyId),
      where('projectId', '==', ratingData.projectId)
    );

    const existingRatings = await getDocs(existingRatingQuery);
    
    if (!existingRatings.empty) {
      throw new Error('You have already rated this student for this project');
    }

    // Create rating document
    const rating = {
      studentId: ratingData.studentId,
      companyId: ratingData.companyId,
      projectId: ratingData.projectId,
      companyName: ratingData.companyName || 'Anonymous Company',
      projectTitle: ratingData.projectTitle || 'Project',
      
      // Ratings
      overallRating: ratingData.overallRating,
      categoryRatings: {
        technicalSkills: ratingData.categoryRatings?.technicalSkills || ratingData.overallRating,
        communication: ratingData.categoryRatings?.communication || ratingData.overallRating,
        workQuality: ratingData.categoryRatings?.workQuality || ratingData.overallRating,
        timeliness: ratingData.categoryRatings?.timeliness || ratingData.overallRating,
        professionalism: ratingData.categoryRatings?.professionalism || ratingData.overallRating
      },
      
      // Review content
      review: ratingData.review || '',
      wouldWorkAgain: ratingData.wouldWorkAgain || false,
      
      // Metadata
      createdAt: serverTimestamp(),
      isVisible: true,
      isApproved: true, // Auto-approve for now, can add moderation later
      
      // Helpful for filtering
      ratingTags: ratingData.tags || []
    };

    const ratingRef = await addDoc(collection(db, 'studentRatings'), rating);

    // Update student's aggregated rating
    await updateStudentRatingAggregate(ratingData.studentId);

    // Create activity log
    await addDoc(collection(db, 'activities'), {
      projectId: ratingData.projectId,
      type: 'rating_submitted',
      description: `Company rated student with ${ratingData.overallRating} stars`,
      ratingId: ratingRef.id,
      studentId: ratingData.studentId,
      companyId: ratingData.companyId,
      timestamp: serverTimestamp()
    });

    // Create notification for student
    try {
      await createRatingNotification(
        ratingData.studentId,
        ratingData.companyName || 'Company',
        ratingData.overallRating,
        ratingData.projectId,
        ratingData.projectTitle || 'Project'
      );
    } catch (notificationError) {
      console.error('Error creating rating notification:', notificationError);
      // Don't fail the rating submission if notification fails
    }

    return ratingRef.id;
  } catch (error) {
    console.error('Error submitting rating:', error);
    throw new Error('Failed to submit rating: ' + error.message);
  }
};

/**
 * Get ratings for a student
 * @param {string} studentId - Student ID
 * @param {number} limitCount - Number of ratings to fetch (default 10)
 * @returns {Promise<Array>} - Array of ratings
 */
export const getStudentRatings = async (studentId, limitCount = 10) => {
  try {
    const ratingsQuery = query(
      collection(db, 'studentRatings'),
      where('studentId', '==', studentId),
      where('isVisible', '==', true),
      where('isApproved', '==', true),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(ratingsQuery);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching student ratings:', error);
    throw new Error('Failed to fetch ratings: ' + error.message);
  }
};

/**
 * Get student's rating aggregate
 * @param {string} studentId - Student ID
 * @returns {Promise<Object>} - Rating aggregate data
 */
export const getStudentRatingAggregate = async (studentId) => {
  try {
    const aggregateDoc = await getDoc(doc(db, 'studentRatingAggregates', studentId));
    
    if (aggregateDoc.exists()) {
      return { id: aggregateDoc.id, ...aggregateDoc.data() };
    }

    // Return default aggregate if none exists
    return {
      studentId,
      totalRatings: 0,
      averageRating: 0,
      categoryAverages: {
        technicalSkills: 0,
        communication: 0,
        workQuality: 0,
        timeliness: 0,
        professionalism: 0
      },
      ratingDistribution: {
        1: 0, 2: 0, 3: 0, 4: 0, 5: 0
      },
      wouldWorkAgainPercentage: 0,
      lastUpdated: null
    };
  } catch (error) {
    console.error('Error fetching rating aggregate:', error);
    throw new Error('Failed to fetch rating aggregate: ' + error.message);
  }
};

/**
 * Update student's aggregated rating data
 * @param {string} studentId - Student ID
 * @returns {Promise<void>}
 */
export const updateStudentRatingAggregate = async (studentId) => {
  try {
    // Get all ratings for the student
    const ratingsQuery = query(
      collection(db, 'studentRatings'),
      where('studentId', '==', studentId),
      where('isVisible', '==', true),
      where('isApproved', '==', true)
    );

    const querySnapshot = await getDocs(ratingsQuery);
    const ratings = querySnapshot.docs.map(doc => doc.data());

    if (ratings.length === 0) {
      // No ratings yet, set defaults
      await updateDoc(doc(db, 'studentRatingAggregates', studentId), {
        studentId,
        totalRatings: 0,
        averageRating: 0,
        categoryAverages: {
          technicalSkills: 0,
          communication: 0,
          workQuality: 0,
          timeliness: 0,
          professionalism: 0
        },
        ratingDistribution: {
          1: 0, 2: 0, 3: 0, 4: 0, 5: 0
        },
        wouldWorkAgainPercentage: 0,
        lastUpdated: serverTimestamp()
      });
      return;
    }

    // Calculate aggregates
    const totalRatings = ratings.length;
    const averageRating = ratings.reduce((sum, rating) => sum + rating.overallRating, 0) / totalRatings;

    // Calculate category averages
    const categoryAverages = {
      technicalSkills: ratings.reduce((sum, rating) => sum + (rating.categoryRatings?.technicalSkills || rating.overallRating), 0) / totalRatings,
      communication: ratings.reduce((sum, rating) => sum + (rating.categoryRatings?.communication || rating.overallRating), 0) / totalRatings,
      workQuality: ratings.reduce((sum, rating) => sum + (rating.categoryRatings?.workQuality || rating.overallRating), 0) / totalRatings,
      timeliness: ratings.reduce((sum, rating) => sum + (rating.categoryRatings?.timeliness || rating.overallRating), 0) / totalRatings,
      professionalism: ratings.reduce((sum, rating) => sum + (rating.categoryRatings?.professionalism || rating.overallRating), 0) / totalRatings
    };

    // Calculate rating distribution
    const ratingDistribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    ratings.forEach(rating => {
      const roundedRating = Math.round(rating.overallRating);
      ratingDistribution[roundedRating]++;
    });

    // Calculate would work again percentage
    const wouldWorkAgainCount = ratings.filter(rating => rating.wouldWorkAgain).length;
    const wouldWorkAgainPercentage = totalRatings > 0 ? (wouldWorkAgainCount / totalRatings) * 100 : 0;

    // Update aggregate document
    const aggregateData = {
      studentId,
      totalRatings,
      averageRating: Math.round(averageRating * 10) / 10, // Round to 1 decimal
      categoryAverages: Object.keys(categoryAverages).reduce((acc, key) => {
        acc[key] = Math.round(categoryAverages[key] * 10) / 10;
        return acc;
      }, {}),
      ratingDistribution,
      wouldWorkAgainPercentage: Math.round(wouldWorkAgainPercentage),
      lastUpdated: serverTimestamp()
    };

    await updateDoc(doc(db, 'studentRatingAggregates', studentId), aggregateData);

  } catch (error) {
    console.error('Error updating rating aggregate:', error);
    throw new Error('Failed to update rating aggregate: ' + error.message);
  }
};

/**
 * Check if company can rate a student
 * @param {string} companyId - Company ID
 * @param {string} studentId - Student ID
 * @param {string} projectId - Project ID
 * @returns {Promise<Object>} - Eligibility info
 */
export const checkRatingEligibility = async (companyId, studentId, projectId) => {
  try {
    // Check if project is completed or student has submitted final work
    const submissionsQuery = query(
      collection(db, 'submissions'),
      where('projectId', '==', projectId),
      where('studentId', '==', studentId),
      where('status', '==', 'approved')
    );

    const submissions = await getDocs(submissionsQuery);
    const hasApprovedSubmissions = !submissions.empty;

    // Check if already rated
    const existingRatingQuery = query(
      collection(db, 'studentRatings'),
      where('studentId', '==', studentId),
      where('companyId', '==', companyId),
      where('projectId', '==', projectId)
    );

    const existingRatings = await getDocs(existingRatingQuery);
    const alreadyRated = !existingRatings.empty;

    return {
      canRate: hasApprovedSubmissions && !alreadyRated,
      hasApprovedSubmissions,
      alreadyRated,
      reason: alreadyRated 
        ? 'Already rated this student for this project'
        : !hasApprovedSubmissions 
          ? 'Student must have approved submissions before rating'
          : 'Eligible to rate'
    };
  } catch (error) {
    console.error('Error checking rating eligibility:', error);
    return {
      canRate: false,
      hasApprovedSubmissions: false,
      alreadyRated: false,
      reason: 'Error checking eligibility'
    };
  }
};

/**
 * Get ratings given by a company
 * @param {string} companyId - Company ID
 * @returns {Promise<Array>} - Array of ratings given
 */
export const getCompanyRatingsGiven = async (companyId) => {
  try {
    const ratingsQuery = query(
      collection(db, 'studentRatings'),
      where('companyId', '==', companyId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(ratingsQuery);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching company ratings:', error);
    throw new Error('Failed to fetch company ratings: ' + error.message);
  }
};

/**
 * Get top-rated students
 * @param {number} limitCount - Number of students to fetch
 * @param {number} minRatings - Minimum number of ratings required
 * @returns {Promise<Array>} - Array of top-rated students
 */
export const getTopRatedStudents = async (limitCount = 10, minRatings = 2) => {
  try {
    const aggregatesQuery = query(
      collection(db, 'studentRatingAggregates'),
      where('totalRatings', '>=', minRatings),
      orderBy('averageRating', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(aggregatesQuery);
    const aggregates = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Fetch student profiles for each aggregate
    const studentsWithRatings = await Promise.all(
      aggregates.map(async (aggregate) => {
        try {
          const studentDoc = await getDoc(doc(db, 'studentProfiles', aggregate.studentId));
          if (studentDoc.exists()) {
            return {
              ...aggregate,
              studentProfile: { id: studentDoc.id, ...studentDoc.data() }
            };
          }
          return aggregate;
        } catch (error) {
          console.error('Error fetching student profile:', error);
          return aggregate;
        }
      })
    );

    return studentsWithRatings;
  } catch (error) {
    console.error('Error fetching top-rated students:', error);
    throw new Error('Failed to fetch top-rated students: ' + error.message);
  }
};

// Utility functions
export const formatRating = (rating) => {
  if (typeof rating !== 'number') return '0.0';
  return rating.toFixed(1);
};

export const getRatingColor = (rating) => {
  if (rating >= 4.5) return '#10b981'; // green
  if (rating >= 4.0) return '#059669'; // dark green
  if (rating >= 3.5) return '#f59e0b'; // yellow
  if (rating >= 3.0) return '#f97316'; // orange
  return '#ef4444'; // red
};

export const getRatingIcon = (rating) => {
  return '★';
};

export const validateRatingInputs = (ratingData) => {
  const errors = [];

  if (!ratingData.overallRating || ratingData.overallRating < 1 || ratingData.overallRating > 5) {
    errors.push('Overall rating must be between 1 and 5');
  }

  if (ratingData.categoryRatings) {
    Object.entries(ratingData.categoryRatings).forEach(([category, rating]) => {
      if (rating < 1 || rating > 5) {
        errors.push(`${category} rating must be between 1 and 5`);
      }
    });
  }

  if (!ratingData.review || ratingData.review.trim().length < 10) {
    errors.push('Review must be at least 10 characters long');
  }

  return errors;
}; 